export type ProjectoStatus = 'Draft' | 'Active' | 'On Hold' | 'Completed' | 'Cancelled';

export type ProjectoItemType =
  | 'Social Media'
  | 'Sponsorship'
  | 'Design'
  | 'Video Production'
  | 'Photography'
  | 'Influencer'
  | 'Talent'
  | 'Venue'
  | 'Equipment'
  | 'Travel'
  | 'PR'
  | 'Other';

export type ProjectoItemPaymentStatus = 'Pending' | 'Invoiced' | 'Paid' | 'Cancelled';

export type ProjectoEntryKind = 'revenue' | 'cost' | 'passthrough';

/** One thing the agency pays for on a line. `amount` is always the total of the row. */
export interface ProjectoPassthroughCost {
  id: string;
  label: string;
  amount: number;
  paid: boolean;
  quantity?: number;
  unit_cost?: number;
}

export type ProjectoInvoiceType = 'Receivable' | 'Payable';

export type ProjectoInvoiceStatus =
  | 'Draft'
  | 'Issued'
  | 'Partially Paid'
  | 'Paid'
  | 'Overdue'
  | 'Cancelled';

export interface ProjectoCampaign {
  id: string;
  name: string;
  client_id: string | null;
  client_name: string;
  brand: string;
  reference: string;
  work_code: string | null;
  status: ProjectoStatus;
  start_date: string | null;
  finish_date: string | null;
  notes: string | null;
  drive_link: string | null;
  budget_enabled: boolean;
  budget_amount: number;
  target_margin_pct: number;
  default_commission_pct: number;
  admin_only: boolean;
  /** Who looks after the project; told when its client invoices are paid. */
  account_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectoCampaignInsert {
  name: string;
  client_id?: string | null;
  client_name?: string;
  brand?: string;
  reference?: string;
  work_code?: string | null;
  status?: ProjectoStatus;
  start_date?: string | null;
  finish_date?: string | null;
  notes?: string | null;
  drive_link?: string | null;
  budget_enabled?: boolean;
  budget_amount?: number;
  target_margin_pct?: number;
  default_commission_pct?: number;
  admin_only?: boolean;
  account_id?: string | null;
  created_by?: string | null;
}

export interface ProjectoPLItem {
  id: string;
  projecto_id: string;
  item_type: ProjectoItemType;
  entry_kind: ProjectoEntryKind;
  passthrough_breakdown: ProjectoPassthroughCost[];
  is_revenue: boolean;
  name: string;
  description: string | null;
  quantity: number;
  unit_price: number;
  total_price: number;
  currency: string;
  margin_pct: number;
  cost_commission_pct: number;
  cost_commission_mode: 'percent' | 'flat';
  cost_commission_flat: number;
  cost_billing_mode: 'absorbed' | 'reimbursed';
  scheduled_date: string | null;
  actual_date: string | null;
  vendor_name: string | null;
  vendor_contact: string | null;
  contract_ref: string | null;
  link: string | null;
  observations: string | null;
  payment_status: ProjectoItemPaymentStatus;
  expected_payment_date: string | null;
  actual_payment_date: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface ProjectoPLItemInsert {
  projecto_id: string;
  item_type?: ProjectoItemType;
  entry_kind?: ProjectoEntryKind;
  passthrough_breakdown?: ProjectoPassthroughCost[];
  is_revenue?: boolean;
  name?: string;
  description?: string | null;
  quantity?: number;
  unit_price?: number;
  currency?: string;
  margin_pct?: number;
  cost_commission_pct?: number;
  cost_commission_mode?: 'percent' | 'flat';
  cost_commission_flat?: number;
  cost_billing_mode?: 'absorbed' | 'reimbursed';
  scheduled_date?: string | null;
  actual_date?: string | null;
  vendor_name?: string | null;
  vendor_contact?: string | null;
  contract_ref?: string | null;
  link?: string | null;
  observations?: string | null;
  payment_status?: ProjectoItemPaymentStatus;
  expected_payment_date?: string | null;
  actual_payment_date?: string | null;
  display_order?: number;
}

export interface ProjectoInvoice {
  id: string;
  projecto_id: string;
  invoice_type: ProjectoInvoiceType;
  number: string | null;
  counterparty_name: string | null;
  counterparty_email: string | null;
  issue_date: string | null;
  due_date: string | null;
  subtotal: number;
  vat_rate: number;
  total: number;
  currency: string;
  status: ProjectoInvoiceStatus;
  amount_paid: number;
  paid_date: string | null;
  pdf_url: string | null;
  notes: string | null;
  is_overdue: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface ProjectoInvoiceInsert {
  projecto_id: string;
  invoice_type: ProjectoInvoiceType;
  number?: string | null;
  counterparty_name?: string | null;
  counterparty_email?: string | null;
  issue_date?: string | null;
  due_date?: string | null;
  subtotal?: number;
  vat_rate?: number;
  total?: number;
  currency?: string;
  status?: ProjectoInvoiceStatus;
  amount_paid?: number;
  paid_date?: string | null;
  pdf_url?: string | null;
  notes?: string | null;
  display_order?: number;
}

export interface ProjectoActivityLog {
  id: string;
  projecto_id: string;
  user_id: string | null;
  user_email: string;
  action: string;
  field_name: string | null;
  old_value: string | null;
  new_value: string | null;
  details: Record<string, unknown>;
  created_at: string;
}

/** What one line means for the project, whatever its kind. */
export interface ProjectoItemFigures {
  kind: ProjectoEntryKind;
  /** What the agency pays. */
  cost: number;
  /** What the client is charged for this line. */
  charge: number;
  /** charge − cost */
  margin: number;
  paidCost: number;
  /** A supplier line charged to the client with no cost recorded yet. */
  missingCost: boolean;
}

/** Every project reads the same way: revenue − costs = margin. */
export interface ProjectoPLSummary {
  fixedBudget: boolean;
  /** What the client pays, without VAT: the budget, or the sum of the lines. */
  revenue: number;
  costs: number;
  margin: number;
  marginPct: number;
  /** Lines charged to the client, also for a fixed budget (where they don't set revenue). */
  linesCharged: number;
  ownServices: number;
  supplierCosts: number;
  supplierCharged: number;
  /** Supplier lines that have a cost: charged − cost. */
  commission: number;
  /** commission as a share of the cost it was added to. */
  commissionPct: number;
  internalCosts: number;
  missingCostLines: number;
  paidCosts: number;
  pendingCosts: number;
  /** Issued to the client, without VAT. */
  invoiced: number;
  /** revenue − invoiced; negative when more was invoiced than the P&L adds up to. */
  toInvoice: number;
  /** Received from the client, without VAT. */
  received: number;
  /** Still to be received, with VAT. */
  outstandingReceivables: number;
  /** Supplier invoices still to pay, with VAT. */
  outstandingPayables: number;
}

export const PROJECTO_STATUS_CONFIG: Record<ProjectoStatus, { label: string; color: string; bg: string }> = {
  Draft:     { label: 'Rascunho',  color: 'text-gray-600',  bg: 'bg-gray-100' },
  Active:    { label: 'Ativo',     color: 'text-emerald-700', bg: 'bg-emerald-100' },
  'On Hold': { label: 'Em pausa',  color: 'text-amber-700', bg: 'bg-amber-100' },
  Completed: { label: 'Concluído', color: 'text-blue-700',  bg: 'bg-blue-100' },
  Cancelled: { label: 'Cancelado', color: 'text-gray-500',  bg: 'bg-gray-100' },
};

export const PROJECTO_ITEM_TYPE_CONFIG: Record<ProjectoItemType, { color: string; bg: string }> = {
  'Social Media':    { color: 'text-sky-700',     bg: 'bg-sky-100' },
  'Sponsorship':     { color: 'text-orange-700',  bg: 'bg-orange-100' },
  'Design':          { color: 'text-pink-700',    bg: 'bg-pink-100' },
  'Video Production':{ color: 'text-violet-700',  bg: 'bg-violet-100' },
  'Photography':     { color: 'text-teal-700',    bg: 'bg-teal-100' },
  'Influencer':      { color: 'text-rose-700',    bg: 'bg-rose-100' },
  'Talent':          { color: 'text-fuchsia-700', bg: 'bg-fuchsia-100' },
  'Venue':           { color: 'text-stone-700',   bg: 'bg-stone-100' },
  'Equipment':       { color: 'text-zinc-700',    bg: 'bg-zinc-100' },
  'Travel':          { color: 'text-cyan-700',    bg: 'bg-cyan-100' },
  'PR':              { color: 'text-lime-700',    bg: 'bg-lime-100' },
  'Other':           { color: 'text-gray-600',    bg: 'bg-gray-100' },
};

export const PROJECTO_ITEM_TYPES: ProjectoItemType[] = [
  'Social Media',
  'Sponsorship',
  'Design',
  'Video Production',
  'Photography',
  'Influencer',
  'Talent',
  'Venue',
  'Equipment',
  'Travel',
  'PR',
  'Other',
];

export const PROJECTO_ITEM_TYPE_LABELS: Record<ProjectoItemType, string> = {
  'Social Media': 'Redes sociais',
  'Sponsorship': 'Patrocínio',
  'Design': 'Design',
  'Video Production': 'Vídeo',
  'Photography': 'Fotografia',
  'Influencer': 'Criador',
  'Talent': 'Talento',
  'Venue': 'Espaço',
  'Equipment': 'Equipamento',
  'Travel': 'Deslocações',
  'PR': 'PR',
  'Other': 'Outro',
};

export const PROJECTO_INVOICE_STATUS_CONFIG: Record<ProjectoInvoiceStatus, { label: string; color: string; bg: string }> = {
  Draft:           { label: 'Rascunho',          color: 'text-gray-600',    bg: 'bg-gray-100' },
  Issued:          { label: 'Emitida',           color: 'text-blue-700',    bg: 'bg-blue-100' },
  'Partially Paid':{ label: 'Paga em parte',     color: 'text-amber-700',   bg: 'bg-amber-100' },
  Paid:            { label: 'Paga',              color: 'text-emerald-700', bg: 'bg-emerald-100' },
  Overdue:         { label: 'Em atraso',         color: 'text-red-700',     bg: 'bg-red-100' },
  Cancelled:       { label: 'Cancelada',         color: 'text-gray-500',    bg: 'bg-gray-50' },
};

/**
 * The campaign status a project reads as, in lists and statistics. Cancelled
 * is intentionally unmapped: a cancelled project must not count as anything.
 */
export const PROJECTO_TO_CAMPAIGN_STATUS: Record<string, 'Consultation' | 'In Progress' | 'Billing' | 'Completed' | undefined> = {
  Draft: 'Consultation',
  Active: 'In Progress',
  'On Hold': 'Billing',
  Completed: 'Completed',
};
